/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { RouteLeavingGuard, Dialog } from 'components/dialogs';
import { dialogStyle } from 'views/savedViews/styles';
import { isEqual } from 'lodash';
import { SavedViewTypes } from 'hooks/savedViews/types';
import { useSavedStateSelectors } from 'hooks/savedViews/hooks';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { Button, Icon, MenuItem, Classes, MenuDivider } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import { IconNames } from '@blueprintjs/icons';

/**
 * ViewSettingsDropDown contains the view selection dropdown
 * as well as the dialogs for navigation away from an unsaved view
 *
 * @param {boolean} showAllViews show views from all datasets?
 * @returns {*}
 * @constructor
 */
export const ViewSettingsDropDown = ({ showAllViews }) => {
	const {
		state: {
			explorer: workspaceExplorer,
			explorer: { selectedDefinitionName },
		},
		dispatch: workspaceDispatch,
	} = useContext(WorkspaceContext);
	let { explorers } = useSavedStateSelectors();

	const history = useHistory();

	const [changeView, setChangeView] = useState(null);
	const [isUnsavedChangesDialogOpen, setUnsavedChangesWarning] = useState(false);
	const [isUnableToSaveDialog, setIsUnableToSaveDialog] = useState(false);

	const openUnsavedChangesWarning = () => setUnsavedChangesWarning(true);
	const closeUnsavedChangesWarning = () => setUnsavedChangesWarning(false);

	const openUnableToSaveDialog = () => setIsUnableToSaveDialog(true);
	const closeUnableToSaveDialog = () => setIsUnableToSaveDialog(false);

	const loadExplorer = useCallback(
		(view) => {
			const formattedView = view;
			workspaceDispatch(WorkspaceActionCreator.loadSavedView('explorer', formattedView));
		},
		[workspaceDispatch]
	);

	const handleViewChange = useCallback(
		(view) => {
			const fullView = [...explorers].filter((x) => x.metaData.id === view.key)[0];
			loadExplorer(fullView);
		},
		[explorers, loadExplorer]
	);

	const handleContinue = useCallback(() => {
		handleViewChange(changeView);
		closeUnsavedChangesWarning();
	}, [changeView, handleViewChange]);

	const selectedViewId = useMemo(() => {
		return workspaceExplorer?.metaData?.id;
	}, [workspaceExplorer]);

	const getIcon = (viewType) => {
		return viewType === SavedViewTypes.Explorer
			? IconNames.GEOSEARCH
			: viewType === SavedViewTypes.Widget
			? IconNames.WIDGET
			: '';
	};

	const clearSavedExplorer = useCallback(() => {
		workspaceDispatch(WorkspaceActionCreator.updateMetaData(undefined));
	}, [workspaceDispatch]);

	const views = useMemo(() => {
		const savedExplorers = explorers
			.map((explorer) => ({
				key: explorer.metaData.id,
				label: explorer.metaData.name,
				type: explorer.metaData.type,
				icon: getIcon(explorer.metaData.type),
				savedExplorer: explorer,
				clearSelection: clearSavedExplorer,
			}))
			.sort((x, y) => {
				// sort by this views matching the current dil, then by dil name, then by view name
				const compareMatchesCurrentDil = (a, b) =>
					(b.savedExplorer.selectedDefinitionName === selectedDefinitionName ? 1 : 0) -
					(a.savedExplorer.selectedDefinitionName === selectedDefinitionName ? 1 : 0);
				const compareDil = (a, b) =>
					a.savedExplorer.selectedDefinitionName.localeCompare(b.savedExplorer.selectedDefinitionName);
				const compareName = (a, b) => a.label.localeCompare(b.label);

				return compareMatchesCurrentDil(x, y) || compareDil(x, y) || compareName(x, y);
			});
		return showAllViews
			? savedExplorers.map((view, index) => {
					if (index === 0) {
						view.separated = true;
						return view;
					}
					const prev = savedExplorers[index - 1];
					return {
						...view,
						separated: prev.savedExplorer.selectedDefinitionName !== view.savedExplorer.selectedDefinitionName,
					};
			  })
			: savedExplorers.filter((explorer) => explorer.savedExplorer.selectedDefinitionName === selectedDefinitionName);
	}, [clearSavedExplorer, explorers, selectedDefinitionName, showAllViews]);

	const checkUnsavedChanges = (view) => {
		if (hasUnsavedChanges()) {
			setChangeView(view);
			openUnsavedChangesWarning();
		} else {
			handleViewChange(view);
		}
	};

	const hasUnsavedChanges = () => {
		if (selectedViewId === undefined) {
			return false;
		}
		const savedExplorer = explorers.filter((x) => x.metaData.id === selectedViewId)[0];
		if (
			isEqual(savedExplorer.filters, workspaceExplorer.filters) &&
			isEqual(savedExplorer.layout, workspaceExplorer.layout)
		) {
			return false;
		} else {
			return true;
		}
	};

	return (
		<React.Fragment>
			<RouteLeavingGuard
				title={'Unsaved Changes'}
				icon={<Icon icon={IconNames.WARNING_SIGN} intent={'warning'} />}
				text={'You are leaving this view. Any unsaved changes will be lost.'}
				when={hasUnsavedChanges()}
				navigate={(path) => history.push(path)}
				shouldBlockNavigation={() => {
					if (hasUnsavedChanges()) {
						return true;
					}
					return false;
				}}
			/>
			{isUnsavedChangesDialogOpen && (
				<UnsavedChangedDialog onClose={closeUnsavedChangesWarning} onClick={handleContinue} />
			)}
			{isUnableToSaveDialog && <UnableToSaveDialog onClose={closeUnableToSaveDialog} />}
			<SelectView
				key={selectedDefinitionName}
				views={views}
				selectedViewId={selectedViewId}
				onSelectionChanged={checkUnsavedChanges}
			/>
		</React.Fragment>
	);
};

// Dropdown for selecting a saved explorer or widget
const SelectView = ({ views, selectedViewId, onSelectionChanged }) => {
	const noSavedViews = views.length === 0;
	const selectedView = views.find((x) => x.key === selectedViewId) || null;

	return (
		<Select
			items={Object.values(views)}
			onItemSelect={onSelectionChanged}
			activeItem={selectedView}
			itemRenderer={ItemRenderer}
			filterable={false}
			disabled={noSavedViews}
			popoverProps={{ minimal: true, fill: true }}
			className={Classes.FILL}
			css={css`
				overflow: hidden;
			`}
		>
			<Button rightIcon={IconNames.CARET_DOWN} disabled={noSavedViews} alignText={'left'} fill>
				{noSavedViews ? 'No Saved Views' : selectedView ? selectedView.label : 'Load Saved View...'}
			</Button>
		</Select>
	);
};

// Each explorer/widget rendered in the SelectView dropdown
// Separate views by selectedDefinitionName
const ItemRenderer = (item, { handleClick, modifiers, index }) => {
	const handleClearSelection = (e) => {
		e.preventDefault();
		item.clearSelection();
	};

	return (
		<div key={index}>
			{item.separated && (
				<React.Fragment>
					{index !== 0 && <MenuDivider />}
					<div css={selectViewTitle}>{item.savedExplorer.selectedDefinitionName}</div>
				</React.Fragment>
			)}
			<div css={menuItemGroup}>
				<MenuItem
					active={modifiers.active}
					disabled={modifiers.disabled}
					onClick={handleClick}
					text={item.label}
					icon={item.icon}
				/>
				{modifiers.active && <Button icon={IconNames.CROSS} minimal onClick={handleClearSelection} />}
			</div>
		</div>
	);
};

const UnsavedChangedDialog = ({ onClose, onClick }) => {
	return (
		<Dialog
			title={'Unsaved Changes'}
			body={
				<div className={Classes.DIALOG_BODY}>
					<p>You are leaving this view. Any unsaved changes will be lost.</p>
				</div>
			}
			icon={<Icon icon={IconNames.WARNING_SIGN} intent={'warning'} />}
			onClose={onClose}
			footer={
				<div className={Classes.DIALOG_FOOTER}>
					<Button
						minimal={true}
						css={css`
							.bp3-button-text {
								color: #5c7080;
							}
						`}
						onClick={onClose}
					>
						Cancel
					</Button>
					<Button outlined={true} onClick={onClick} intent="primary">
						Continue
					</Button>
				</div>
			}
		/>
	);
};

const UnableToSaveDialog = ({ onClose }) => {
	return (
		<Dialog
			style={dialogStyle}
			body={
				<div className={Classes.DIALOG_BODY}>
					<p>Select a view in order to save.</p>
				</div>
			}
			icon={<Icon icon={IconNames.MANUALLY_ENTERED_DATA} intent={'none'} />}
			onClose={onClose}
			footer={
				<div className={Classes.DIALOG_FOOTER}>
					<Button
						minimal={true}
						css={css`
							.bp3-button-text {
								color: #5c7080;
							}
						`}
						onClick={onClose}
					>
						Cancel
					</Button>
				</div>
			}
		/>
	);
};

const selectViewTitle = css`
	text-align: center;
	font-weight: bold;
`;

const menuItemGroup = css`
	width: 100%;
	display: flex;
	justify-content: space-between;

	> li {
		width: 100%;
	}
`;
